import { useMemo, useState } from 'react';
import type {
  ThroughputGroupSeries,
  ThroughputSeriesGranularity,
  ThroughputSeriesPoint,
} from '@nadoutong/contracts';
import { TREND_TONE_CYCLE, TrendChart } from '../../components/charts/TrendChart.tsx';
import type { TrendChartSeries } from '../../components/charts/TrendChart.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Panel } from '../../ui/index.ts';

/**
 * Throughput (RPM/TPM) trend panel for the 平台 SKU / 上游 SKU hub tabs
 * (#1698) — the traffic twin of `GroupTrendsPanel`: one combined
 * comparison chart where every group is a line series with a
 * click-to-toggle legend and the same pristine top-N default
 * visibility. Deliberately chart-only (no 表格 twin): the bucket table
 * rolls day buckets up by SUMMING, which is wrong for per-minute
 * averages (a weekly rollup would need duration-weighted averaging),
 * so reusing it would quietly lie — dropped rather than forked.
 *
 * Displayed values are per-bucket AVERAGES per minute: each bucket's
 * raw count divided by the minutes the bucket ACTUALLY overlaps the
 * resolved query window (`bucketDivisorMinutes`) — interior buckets
 * divide by the full bucket length (minute = 1, hour = 60,
 * day = 1440), but the first and last buckets of a window divide by
 * their real coverage, so a `today` window's in-progress current-hour
 * bucket is not deflated by up to 60x. RPM divides `request_count`;
 * TPM divides the sum of all four token columns (input + output +
 * cache read/write) — the note under the title states the
 * conventions, including that throughput counts EVERY request
 * (failures and unpriced calls included; a different basis from the
 * money sections' 调用数).
 */

const COUNT_FORMATTER = new Intl.NumberFormat('en-US');

/**
 * Full bucket length in minutes per granularity. String-keyed on
 * purpose: `granularity` arrives from a server response, so an
 * anomalous runtime value must fall back (callers use `?? 60`) instead
 * of poisoning every series with NaN.
 */
const MINUTES_PER_BUCKET: Readonly<Record<string, number>> = {
  minute: 1,
  hour: 60,
  day: 1440,
};

/**
 * Effective RPM/TPM divisor for one bucket: the minutes the bucket
 * overlaps the resolved `[from, to)` window. Bucket labels are UTC+8
 * wall-clock (`YYYY-MM-DD`, `YYYY-MM-DD HH:00`, `YYYY-MM-DD HH:MM`) —
 * parsed back to an absolute interval with an explicit `+08:00`
 * offset. Floored at 1 minute (zero-division guard); any unparseable
 * input falls back to the full bucket length, which is also the exact
 * answer for every interior bucket. Exported pure for unit tests.
 */
export function bucketDivisorMinutes(
  bucket: string,
  granularity: ThroughputSeriesGranularity,
  windowFromMs: number,
  windowToMs: number,
): number {
  const fullMinutes = MINUTES_PER_BUCKET[granularity] ?? 60;
  const startMs = Date.parse(
    granularity === 'day'
      ? `${bucket}T00:00:00+08:00`
      : `${bucket.slice(0, 10)}T${bucket.slice(11)}:00+08:00`,
  );
  if (!Number.isFinite(startMs) || !Number.isFinite(windowFromMs) || !Number.isFinite(windowToMs)) {
    return fullMinutes;
  }
  const endMs = startMs + fullMinutes * 60_000;
  const overlapMinutes = (Math.min(endMs, windowToMs) - Math.max(startMs, windowFromMs)) / 60_000;
  if (overlapMinutes >= fullMinutes) {
    return fullMinutes;
  }
  return Math.max(1, overlapMinutes);
}

export type ThroughputMetric = 'rpm' | 'tpm';

const THROUGHPUT_METRIC_OPTIONS: readonly ThroughputMetric[] = ['rpm', 'tpm'];
const THROUGHPUT_METRIC_KEYS: Record<ThroughputMetric, I18nKey> = {
  rpm: 'metrics.throughput.metric.rpm',
  tpm: 'metrics.throughput.metric.tpm',
};

/** Same pristine-default visibility cap as `GroupTrendsPanel`. */
const DEFAULT_VISIBLE_LIMIT = 10;

/**
 * Per-minute averages read differently at different magnitudes: below
 * 1, two decimals keep sparse groups distinguishable (0.03 vs 0.30
 * would otherwise both collapse to 0.0); below 10 the first decimal IS
 * the signal (2.4 vs 2.0 req/min); at 10+ fractions are noise, so
 * values round to grouped integers (COUNT_FORMATTER style).
 */
function formatPerMinute(value: number): string {
  if (value < 1) {
    return value.toFixed(2);
  }
  return value < 10 ? value.toFixed(1) : COUNT_FORMATTER.format(Math.round(value));
}

/** One bucket's metric value: raw count ÷ bucket minutes. */
function pointPerMinute(
  point: ThroughputSeriesPoint,
  metric: ThroughputMetric,
  minutesPerBucket: number,
): number {
  if (metric === 'rpm') {
    return point.request_count / minutesPerBucket;
  }
  return (
    (point.input_tokens +
      point.output_tokens +
      point.cache_read_tokens +
      point.cache_write_tokens) /
    minutesPerBucket
  );
}

/**
 * One group (platform SKU / upstream) with its display name resolved —
 * the throughput twin of `MetricsGroup` (the tabs already own the
 * name-join maps, so the panel takes resolved names instead of
 * re-deriving them).
 */
export interface ThroughputGroup {
  /** Raw group identifier (`sku_id` / `upstream_id`) or `'(unknown)'`. */
  readonly key: string;
  /** Resolved display name; falls back to the raw identifier. */
  readonly name: string;
  readonly series: ThroughputGroupSeries;
}

export function ThroughputTrendsPanel({
  title,
  groups,
  granularity,
  windowFrom,
  windowTo,
  emptyLabel,
  testIdPrefix,
}: {
  readonly title: string;
  readonly groups: readonly ThroughputGroup[];
  readonly granularity: ThroughputSeriesGranularity;
  /**
   * Resolved window bounds from the response envelope (always concrete
   * ISO strings — the server materialises an omitted `to` into "now"),
   * so partial first/last buckets divide by their real coverage
   * without any client-side "now" approximation.
   */
  readonly windowFrom: string;
  readonly windowTo: string;
  readonly emptyLabel: string;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  const [metric, setMetric] = useState<ThroughputMetric>('rpm');
  // `null` = pristine default (top-N by request count visible — the API
  // ranks series request-count DESC). Same materialise-on-first-toggle
  // idiom as `GroupTrendsPanel`.
  const [hiddenOverride, setHiddenOverride] = useState<ReadonlySet<string> | null>(null);

  const defaultHidden = useMemo(
    () => new Set(groups.slice(DEFAULT_VISIBLE_LIMIT).map((group) => group.key)),
    [groups],
  );
  const hiddenKeys = hiddenOverride ?? defaultHidden;
  const toggleSeries = (key: string): void => {
    setHiddenOverride((previous) => {
      const next = new Set(previous ?? defaultHidden);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const bucketLabels = groups[0]?.series.points.map((point) => point.bucket) ?? [];
  // Sub-day buckets are labelled `YYYY-MM-DD HH:MM`; when the whole
  // window sits inside one UTC+8 day the date part is pure noise, so
  // the axis shows the bare time (same rule as `GroupTrendsPanel`).
  const singleDay =
    granularity !== 'day' &&
    bucketLabels.length > 0 &&
    bucketLabels.every((label) => label.slice(0, 10) === bucketLabels[0]?.slice(0, 10));
  const displayLabels = singleDay ? bucketLabels.map((label) => label.slice(11)) : bucketLabels;

  const windowFromMs = Date.parse(windowFrom);
  const windowToMs = Date.parse(windowTo);
  const chartSeries: readonly TrendChartSeries[] = groups.map((group, index) => ({
    key: group.key,
    label: group.name,
    kind: 'line',
    tone: TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral',
    values: group.series.points.map((point) =>
      pointPerMinute(
        point,
        metric,
        bucketDivisorMinutes(point.bucket, granularity, windowFromMs, windowToMs),
      ),
    ),
  }));

  return (
    <Panel
      caption={title}
      count={groups.length}
      actions={
        <div
          role="tablist"
          aria-label={t('metrics.throughput.metric_aria')}
          className="inline-flex gap-1"
        >
          {THROUGHPUT_METRIC_OPTIONS.map((option) => {
            const active = option === metric;
            return (
              <Button
                key={option}
                role="tab"
                aria-selected={active}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                className={active ? 'bg-active text-paper' : undefined}
                data-testid={`${testIdPrefix}-throughput-metric-${option}`}
                onClick={() => {
                  setMetric(option);
                }}
              >
                {t(THROUGHPUT_METRIC_KEYS[option])}
              </Button>
            );
          })}
        </div>
      }
      padded
      testId={`${testIdPrefix}-throughput-trends`}
    >
      {groups.length === 0 ? (
        <p
          className="m-0 text-body text-paper-low"
          data-testid={`${testIdPrefix}-throughput-empty`}
        >
          {emptyLabel}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p
            className="m-0 text-micro text-paper-low"
            data-testid={`${testIdPrefix}-throughput-note`}
          >
            {t('metrics.throughput.note')}
          </p>
          {groups.length > DEFAULT_VISIBLE_LIMIT && hiddenOverride === null ? (
            <p
              className="m-0 text-micro text-paper-low"
              data-testid={`${testIdPrefix}-throughput-default-visible-note`}
            >
              {t('metrics.throughput.default_visible_note', { count: DEFAULT_VISIBLE_LIMIT })}
            </p>
          ) : null}
          <TrendChart
            labels={displayLabels}
            series={chartSeries}
            hiddenKeys={hiddenKeys}
            onToggleSeries={toggleSeries}
            legendExtra={
              <span className="inline-flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`${testIdPrefix}-throughput-legend-show-all`}
                  onClick={() => {
                    setHiddenOverride(new Set());
                  }}
                >
                  {t('metrics.chart.legend.show_all')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`${testIdPrefix}-throughput-legend-hide-all`}
                  onClick={() => {
                    setHiddenOverride(new Set(groups.map((group) => group.key)));
                  }}
                >
                  {t('metrics.chart.legend.hide_all')}
                </Button>
              </span>
            }
            valueFormatter={formatPerMinute}
            ariaLabel={title}
            emptyLabel={emptyLabel}
            testId={`${testIdPrefix}-throughput-chart`}
          />
        </div>
      )}
    </Panel>
  );
}
